<template>
  <v-navigation-drawer
    v-model="drawerOpen"
    stateless
    :hide-overlay="hideOverlay"
    fixed
    temporary
    right
    width="750"
  >
    <div class="RightHeader d-flex flex-column">
      <div class="RightHeader_content flextabLabels() {-grow-1 mt-22 pt-10 pl-8 pr-10">
        <RightDrawerHeader
          class="mt-14 mb-2 pt-8"
          :title="$t(`main.enums.sections.${activeTab}`)"
          :person="person"
          :editingMode="editionMode"
          @close="$emit('close')"
        />

        <div class="RightHeader_content_header">
          <CompletionBlock
            v-if="!editionMode"
            class="my-8"
            :completion="person.meta && person.meta.completion_rate"
          />
          <Warnings
            v-if="hasWarnings"
            :warnings="warnings"
            noBackground
            class="mt-4"
            @selectTab="goToTab"
          />
        </div>

        <v-divider
          v-show="editionMode"
          class="mt-8"
        />
        <div v-show="!editionMode">
          <Tabs
            v-model="tabIndex"
            class="mt-8 mx-0"
            :tabs="tabLabels"
          />
        </div>

        <div
          v-if="!editionMode"
          class="pt-5"
        >
          <PersonTabPerso v-if="activeTab === TABS.perso" />
          <PersonTabContact v-if="activeTab === TABS.contact" />
          <PersonTabBank v-if="activeTab === TABS.bank" />
          <PersonTabDocs
            v-if="activeTab === TABS.docs"
            isEditMode
            @updated="onDocumentUpdated"
          />
        </div>

        <div
          v-if="editionMode"
          class="pt-5"
        >
          <EditPersonTabPerso
            v-show="activeTab === TABS.perso"
            ref="edit1"
            :formValid.sync="formValidPerso"
          />
          <EditPersonTabContact
            v-show="activeTab === TABS.contact"
            ref="edit2"
            :formValid.sync="formValidContact"
          />
          <EditPersonTabBank
            v-show="activeTab === TABS.bank"
            ref="edit3"
            :formValid.sync="formValidBank"
          />
        </div>
      </div>
      <div
        v-if="showFooter"
        class="footer"
      >
        <v-row
          v-if="activeTab !== TABS.docs"
          class="d-flex justify-center py-8"
        >
          <div v-if="editionMode">
            <div>
              <v-btn
                text
                outlined
                class="theme-btn app-button-secondary color-white nice-padding mr-8 "
                @click="onClickButton('cancel')"
              >{{ $t('common.action.cancel') }}</v-btn>
              <v-btn
                text
                :disabled="!isValid"
                class="theme-btn app-button-primary color nice-padding"
                @click="onClickButton('save')"
              >{{ $t('common.action.save') }}</v-btn>
            </div>
          </div>
          <div v-else>
            <v-btn
              text
              block
              class="theme-btn app-button-primary nice-padding"
              @click="onClickButton('update')"
            >{{ $t('common.action.update') }}
            </v-btn>
          </div>
        </v-row>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script type="text/javascript">
  import mixinUi from '@/mixins/utils/ui.js'
  import CommonMixin from '@/mixins/store/CommonMixin'
  import {
    createPersonPayload, cleanPersonPayload, mapWarnings 
  } from '@/utils/person.utils'
  import { compareObjects } from '@/utils/common.utils'

  import Tabs from '@/components/ui/common/Tabs/Tabs'
  import RightDrawerHeader from '@/components/RightDrawer/header/RightDrawerHeader'

  import CompletionBlock from '@/components/RightDrawer/header/CompletionBlock'
  import Warnings from '@/components/RightDrawer/header/Warnings'

  import ApiService from '@/services/ApiService'
  import { ACTIONS, TABS } from '@/components/RightDrawer/helpers/PersonTab.const'

  import PersonTabPerso from '@/components/RightDrawer/display/PersonTabPerso'
  import PersonTabContact from '@/components/RightDrawer/display/PersonTabContact'
  import PersonTabBank from '@/components/RightDrawer/display/PersonTabBank'
  import PersonTabDocs from '@/components/RightDrawer/display/PersonTabDocs'

  import EditPersonTabPerso from '@/components/RightDrawer/form/EditPersonTabPerso'
  import EditPersonTabContact from '@/components/RightDrawer/form/EditPersonTabContact'
  import EditPersonTabBank from '@/components/RightDrawer/form/EditPersonTabBank'

  import { getTabs } from '@/components/RightDrawer/helpers/PersonTab.helper'
  import {
    AUTHORIZATIONS, PERSON_STATUS, PERSON_STATUS_AUTHORIZATION 
  } from '@/services/models.const'

  export default {
    components: {
      CompletionBlock,
      RightDrawerHeader,
      Tabs,
      PersonTabPerso,
      PersonTabContact,
      PersonTabBank,
      PersonTabDocs,
      EditPersonTabPerso,
      EditPersonTabContact,
      EditPersonTabBank,
      Warnings,
    },
    mixins: [mixinUi, CommonMixin,],
    props: {
      person: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        activeTab: TABS.perso,
        editionMode: false,
        formValidPerso: true,
        formValidContact: true,
        formValidBank: true,
        warnings: {},
        TABS,
      }
    },
    computed: {
      isReadOnly() {
        // @todo should be mutualised in commonMixin

        const customerFileStatus = this.currentUser.meta.customer_file_status
        const authorization = PERSON_STATUS_AUTHORIZATION[customerFileStatus]

        return authorization === AUTHORIZATIONS.readOnly
      },
      showFooter() {
        if (this.isReadOnly) {
          return false
        }

        return ![PERSON_STATUS.validated_by_advisor, PERSON_STATUS.validated_by_user,].includes(
          this.currentUser.meta.customer_file_status
        )
      },
      tabs() {
        return getTabs(this.requiredAttributes, this.optionalAttributes)
      },
      tabIndex: {
        get() {
          return this.tabs.indexOf(this.activeTab)
        },
        set(index) {
          this.activeTab = this.tabs[index]
        },
      },
      isValid() {
        if (this.activeTab === TABS.perso) {
          return this.formValidPerso
        }
        if (this.activeTab === TABS.contact) {
          return this.formValidContact
        }
        if (this.activeTab === TABS.bank) {
          return this.formValidBank
        }

        return true
      },
      tabLabels() {
        return this.tabs.map((tab, index) => {
          return this.$t(`main.enums.tabs.${tab}`)
        })
      },
      hideOverlay() {
        return !this.editionMode
      },
      hasWarnings() {
        return (
          !!this.selectedProfile.meta.missing_attributes.length ||
          !!this.selectedProfile.meta.missing_documents.length ||
          !!this.selectedProfile.meta.refused_attributes.length ||
          !!this.selectedProfile.meta.refused_documents.length
        )
      },
    },
    mounted() {
      this.refreshWarnings()
    },
    methods: {
      goToTab(id) {
        this.tabIndex = id
      },
      async onDocumentUpdated() {
        await this.refreshSelectedProfile(true)
        this.refreshWarnings()
      },
      refreshWarnings() {
        if (!this.hasWarnings) return
        this.warnings = this.getWarnings({
          missingAttributes: this.selectedProfile.meta.missing_attributes,
          missingDocuments: this.selectedProfile.meta.missing_documents,
          refusedAttributes: this.selectedProfile.meta.refused_attributes,
          refusedDocuments: this.selectedProfile.meta.refused_documents,
        })
      },
      toggleEditionMode() {
        this.editionMode = !this.editionMode
      },
      getRecord() {
        if (this.activeTab === TABS.perso) {
          return this.$refs.edit1.getRecord()
        }
        if (this.activeTab === TABS.contact) {
          return this.$refs.edit2.getRecord()
        }
        if (this.activeTab === TABS.bank) {
          return this.$refs.edit3.getRecord()
        }
        if (this.activeTab === TABS.docs) {
          return null
        }
        throw new Error(`Unknown tab '${this.activeTab}'`)
      },
      async onClickButton(actionId) {
        if (actionId === ACTIONS.update) {
          this.editionMode = !this.editionMode

          return
        } else if (actionId === ACTIONS.cancel) {
          this.editionMode = !this.editionMode
          this.$store.commit('profile/resetFormErrors')
          await this.refreshSelectedProfile(true)

          return
        } else if (actionId === ACTIONS.save) {
          const originalPayload = this.selectedProfile
          const newPayload = this.getRecord()
          const diff = compareObjects(originalPayload, newPayload)

          if (Object.keys(diff).length > 0) {
            let payload = createPersonPayload(diff, this.requiredAttributes, this.optionalAttributes)
            payload = cleanPersonPayload(payload)
            this.toggleAppLoader(true)
            const result = await ApiService.updatePerson(this.selectedProfileIndex, payload)
            this.toggleAppLoader(false)
            if (result.errors) {
              this.$message({
                type: 'error',
                text: 'update_infos_error',
              })
              this.$store.commit('profile/setFormErrors', result.errors)
            } else {
              this.$message({
                type: 'success',
                text: 'update_infos_success',
              })
              await this.refreshSelectedProfile(true)
              this.$store.commit('profile/resetFormErrors')
              this.editionMode = !this.editionMode
              this.refreshWarnings()
            }
          } else {
            this.$message({
              type: 'warning',
              text: 'no_changes',
            })
          }

          return
        }

        throw new Error(`Unmanaged action '${actionId}'`)
      },

      closeDrawer() {
        this.$store.commit('profile/closeDrawer')
        this.$emit('close')
      },
      isPayloadModified() {
        const originalPayload = [this.currentUser, ...this.peopleDetails,].find(
          (item) => item.id === this.selectedProfileIndex
        )
        const newPayload = this.selectedProfile
        const diff = compareObjects(originalPayload, newPayload)

        return Object.keys(diff).length > 0
      },
      getWarnings(fields) {
        const requiredData = {
          requiredAttributes: this.requiredAttributes,
          requiredDocuments: this.requiredDocuments,
        }

        return mapWarnings(fields, requiredData)
      },
    },
  }
</script>

<style scoped lang="scss">
.RightHeader {
  height: calc(100%);
  box-shadow: 0 16px 32px 0 $color-31_54_139_12;
  &_content {
    overflow: auto;
    height: calc(100%);
    &_header {
      background: rgba(0, 195, 241, 0.04);
    }
  }
  .footer {
    box-shadow: 0 -4px 8px 0 rgba(31, 54, 139, 0.08);
  }
}
</style>
